import type { CodePromptAttachment } from '@/data/code-sessions/types';
import type { CodeAttachmentRef } from '@/lib/code-sessions/attachment-types';

import { isPreviewedAttachment } from '@/lib/code-sessions/attachment-types';

/**
 * A file in the composer: one this tab uploads, whose progress the composer
 * tracks by `key`, or a saved one that a restored prompt brought back, with
 * its route, the `data:` URL of the copy OpenCode stores on the person's own
 * computer, or, after a refused send, the image this tab still holds.
 */
export type ComposerAttachment = {
  /** Unique in the composer: a saved file's ID, or a local ID for an upload. */
  key: string;
  name: string;
  contentType: string;
  size: number;
  /** The attachment route a saved file loads from. */
  url?: string;
  /**
   * A `data:` URL of the copy OpenCode stores on the person's own computer, which a thumbnail
   * shows and nothing opens.
   */
  dataUrl?: string;
} & ({ kind: 'saved'; id: string; file?: Blob } | { kind: 'local'; file: File });

export type ComposerAttachmentStatus =
  | { status: 'uploading'; progress: number }
  | { status: 'failed'; error: string }
  | { status: 'ready'; ref: CodeAttachmentRef };

/**
 * What an image's thumbnail shows: its saved route, the copy OpenCode stores
 * on the person's own computer, or the bytes this tab holds. None for any other
 * file, which shows by name.
 */
export function thumbnailSource(attachment: {
  contentType: string;
  url?: string;
  dataUrl?: string;
  file?: Blob;
}): string | Blob | undefined {
  return isPreviewedAttachment(attachment.contentType)
    ? (attachment.url ?? attachment.dataUrl ?? attachment.file)
    : undefined;
}

/** A sent file back in the composer, as Edit from here or a refused send returns it. */
export function restoredAttachment(attachment: CodePromptAttachment): ComposerAttachment {
  return {
    key: attachment.id,
    kind: 'saved',
    id: attachment.id,
    name: attachment.name,
    contentType: attachment.contentType,
    size: attachment.size,
    ...(attachment.url === undefined ? {} : { url: attachment.url }),
    ...(attachment.dataUrl === undefined ? {} : { dataUrl: attachment.dataUrl }),
    ...(attachment.file === undefined ? {} : { file: attachment.file }),
  };
}

/** What the prompt carries for a file whose upload finished: an image keeps its bytes for its thumbnail. */
export function promptAttachment(
  attachment: ComposerAttachment,
  ref: CodeAttachmentRef,
): CodePromptAttachment {
  return {
    id: ref.id,
    name: ref.name,
    contentType: attachment.contentType,
    size: attachment.size,
    ...(attachment.url === undefined ? {} : { url: attachment.url }),
    ...(attachment.dataUrl === undefined ? {} : { dataUrl: attachment.dataUrl }),
    ...(attachment.file !== undefined && isPreviewedAttachment(attachment.contentType)
      ? { file: attachment.file }
      : {}),
  };
}
